import { PATH_DASHBOARD } from '../../routes/paths';
import {
  AccountBalance,
  Storage,
  Assignment,
  Group,
  Groups,
  School,
  Mail,
  Flag,
  Delete,
  People,
} from '@mui/icons-material';
import {
  BusinessCenter,
  FileCopy,
  Handshake,
  CardMembership,
  Palette,
  Campaign,
} from '@mui/icons-material';
import VolunteerActivism from '@mui/icons-material/VolunteerActivism';
import {
  Public,
  AutoStories,
  RunningWithErrors,
  LocalShipping,
  Inventory2Rounded,
  WarehouseRounded,
  Report,
  FactCheck,
  Search,
  EventBusyRounded,
  Equalizer,
  Warning,
  NotificationsActive,
  Settings,
  Star,
  MenuBook,
} from '@mui/icons-material';
import SvgIconStyle from '../../components/SvgIconStyle';
import { Icon } from '@iconify/react';
import sharpFoodBank from '@iconify/icons-ic/sharp-food-bank';
import truckIcon from '@iconify/icons-ant-design/car-filled';
import { IncomingRequestsBadge } from 'src/views/Donations/IncomingRequests';
import { OnboardingRequestsBadge } from 'src/views/Partnerships/Requests';
import { InvitationsBadge } from 'src/views/Invitations';
import { useTranslation } from 'react-i18next';
import { getActivePlan } from 'src/hooks/useAuth';
import useAuth from 'src/hooks/useAuth';
import {
  hasPlanCapability,
  isOpsPlan,
  hasEffectiveHomeDelivery,
  normalizePlanKey,
  getPlanBySubscriptionProductId,
} from 'careit';
import { getAdminImpersonation } from 'src/utils/adminImpersonation';
import useInvites from 'src/hooks/useInvites';
import { useState, useEffect, useCallback, useRef } from 'react';
import { getTeamCorporateInfo } from 'src/backend/team';
import {
  getCorporateOnboardingIntakeRequests,
  getPendingApprovals,
  getPendingOrgUpdates,
  getMarketplaceSellerApplications,
  getOpenComplaintCount,
} from 'src/backend/admin';
import { useFreshcueOrganizations } from 'src/hooks/freshcue/useFreshcueOrganizations';
import {
  isOnboardingEnabled,
  isInspectionsEnabled,
  isComplaintsEnabled,
  isNovEnabled,
  isPenaltiesEnabled,
  isSchedulingEnabled,
  isContactLogEnabled,
  isDonationsEnabled,
} from 'src/utils/featureFlags';
import {
  canUseDistributionAdmin as canUseDistributionAdminForUser,
  canUseDistributions as canUseDistributionsForUser,
  isDistributionDriverOnlyUser,
} from 'src/utils/distributionAccess';
import { Chip } from '@mui/material';
import useNonprofitMarketplaceRelevance from 'src/hooks/useNonprofitMarketplaceRelevance';
import * as distributionsApi from 'src/backend/distributions';

const QUO_STATUS_URL = 'https://status.quo.com';

// Marks menu items only owner super admins can access.
const ownerOnlyStar = <Star sx={{ fontSize: 14, color: 'warning.dark' }} />;

const getIcon = (name) => (
  <SvgIconStyle
    src={`/static/icons/navbar/${name}.svg`}
    sx={{ width: 22, height: 22 }}
  />
);

const ICONS = {
  map: getIcon('ic_map'),
  blog: getIcon('ic_blog'),
  chat: getIcon('ic_chat'),
  mail: getIcon('ic_mail'),
  page: getIcon('ic_page'),
  user: getIcon('ic_user'),
  copy: getIcon('ic_copy'),
  error: getIcon('ic_error'),
  charts: getIcon('ic_charts'),
  editor: getIcon('ic_editor'),
  upload: getIcon('ic_upload'),
  company: getIcon('ic_company'),
  animate: getIcon('ic_animate'),
  calendar: getIcon('ic_calendar'),
  elements: getIcon('ic_elements'),
  carousel: getIcon('ic_carousel'),
  language: getIcon('ic_language'),
  ecommerce: getIcon('ic_ecommerce'),
  freshcue: getIcon('ic_freshcue'),
  analytics: getIcon('ic_analytics'),
  dashboard: getIcon('ic_dashboard'),
  components: getIcon('ic_components'),
  authenticator: getIcon('ic_authenticator'),
  reports: getIcon('ic_assessment'),
  donation: <Inventory2Rounded />,
  inventory: <WarehouseRounded />,
  vehicle: <Icon icon={truckIcon} width={24} height={24} />,
  foodBank: <Icon icon={sharpFoodBank} width={24} height={24} />,
  search: <Search />,
  assignment: <Assignment />,
  groups: <Group />,
  cancellationReport: <EventBusyRounded />,
  equalizer: <Equalizer />,
  invitations: <Mail />,
  nov: <Flag />,
  delete: <Delete />,
  warning: <Warning />,
  notificationMonitor: <NotificationsActive />,
};

// Buyer items are shared by every marketplace menu variant: any org can
// browse and buy, regardless of whether it sells.
const getMarketplaceBuyerItems = (t) => [
  {
    title: t('marketplace.nav.browse', {
      defaultValue: 'Browse Listings',
    }),
    href: PATH_DASHBOARD.findItems.marketplace,
  },
  {
    title: t('marketplace.nav.my-orders', {
      defaultValue: 'My Orders',
    }),
    href: PATH_DASHBOARD.marketplace.myOrders,
  },
  {
    title: t('marketplace.nav.messages', {
      defaultValue: 'Messages',
    }),
    href: PATH_DASHBOARD.marketplace.messages,
  },
];

const getMarketplaceSidebarItem = ({ user, t }) => {
  // Suspended sellers keep their seller nav (My Listings / Incoming Orders) so
  // they can still fulfill in-flight orders they're obligated on — the backend
  // permits those actions and only blocks creating/editing listings. Suspension
  // sets marketplace_enabled = 0, so gate seller items on the seller STATUS too.
  const isSeller =
    user.marketplace_enabled || user.marketplace_seller_status === 'suspended';
  return {
    title: t('marketplace.title'),
    icon: ICONS.ecommerce,
    href: isSeller
      ? PATH_DASHBOARD.marketplace.myListings
      : PATH_DASHBOARD.findItems.marketplace,
    items: isSeller
      ? [
          ...getMarketplaceBuyerItems(t),
          {
            title: t('marketplace.nav.my-listings', {
              defaultValue: 'My Listings',
            }),
            href: PATH_DASHBOARD.marketplace.myListings,
          },
          {
            title: t('marketplace.nav.incoming-orders', {
              defaultValue: 'Incoming Orders',
            }),
            href: PATH_DASHBOARD.marketplace.incomingOrders,
          },
        ]
      : getMarketplaceBuyerItems(t),
  };
};

const getMarketplaceBuyerSidebarItem = ({ t }) => ({
  title: t('marketplace.title'),
  icon: ICONS.ecommerce,
  href: PATH_DASHBOARD.findItems.marketplace,
  items: getMarketplaceBuyerItems(t),
});

const useSidebarConfig = () => {
  const { t } = useTranslation('translation');
  const { user, isFoodMoverFull, hasHomeDelivery, plan } = useAuth();
  const { show: marketplaceRelevant } = useNonprofitMarketplaceRelevance();
  const onboardingEnabled = isOnboardingEnabled(user);
  const { invites = [] } = useInvites(user?.email || null);
  const [distributionGeocodeIssues, setDistributionGeocodeIssues] = useState(0);
  const [corporateInfo, setCorporateInfo] = useState(null);
  const [pendingApprovalQueueCount, setPendingApprovalQueueCount] = useState(0);
  const [pendingOrgUpdateQueueCount, setPendingOrgUpdateQueueCount] =
    useState(0);
  const [
    pendingCorporateOnboardingQueueCount,
    setPendingCorporateOnboardingQueueCount,
  ] = useState(0);
  const [pendingSellerApplicationsCount, setPendingSellerApplicationsCount] =
    useState(0);
  const [openComplaintQueueCount, setOpenComplaintQueueCount] = useState(0);
  const isFetchingQueueCountsRef = useRef(false);
  const queuedQueueRefreshRef = useRef(false);
  const fetchQueueCounts = useCallback(async () => {
    if (isFetchingQueueCountsRef.current) {
      queuedQueueRefreshRef.current = true;
      return;
    }

    if (user?.account_type !== 'ADMIN') {
      setPendingApprovalQueueCount(0);
      setPendingOrgUpdateQueueCount(0);
      setPendingCorporateOnboardingQueueCount(0);
      setPendingSellerApplicationsCount(0);
      setOpenComplaintQueueCount(0);
      return;
    }

    isFetchingQueueCountsRef.current = true;
    try {
      const [
        approvalQueue,
        orgUpdateQueue,
        onboardingIntakeQueue,
        sellerApplications,
        openComplaints,
      ] = await Promise.all([
        getPendingApprovals(),
        getPendingOrgUpdates(),
        getCorporateOnboardingIntakeRequests({ status: 'pending' }),
        getMarketplaceSellerApplications({ status: 'pending', step: 1 }),
        getOpenComplaintCount(),
      ]);
      setPendingApprovalQueueCount(approvalQueue?.entries?.length || 0);
      setPendingOrgUpdateQueueCount(orgUpdateQueue?.entries?.length || 0);
      setPendingCorporateOnboardingQueueCount(
        onboardingIntakeQueue?.length || 0
      );
      setPendingSellerApplicationsCount(sellerApplications?.count || 0);
      setOpenComplaintQueueCount(openComplaints?.total || 0);
    } catch (error) {
      setPendingApprovalQueueCount(0);
      setPendingOrgUpdateQueueCount(0);
      setPendingCorporateOnboardingQueueCount(0);
      setPendingSellerApplicationsCount(0);
      setOpenComplaintQueueCount(0);
      console.error('Failed to fetch queue badge counts:', error);
    } finally {
      isFetchingQueueCountsRef.current = false;
      if (queuedQueueRefreshRef.current) {
        queuedQueueRefreshRef.current = false;
        fetchQueueCounts();
      }
    }
  }, [user?.account_type]);

  useEffect(() => {
    if (user?.team_id) {
      getTeamCorporateInfo(user.team_id)
        .then(setCorporateInfo)
        .catch((error) => {
          console.error('Failed to fetch corporate info:', error);
          setCorporateInfo({ isPartOfACorporate: false });
        });
    }
  }, [user?.team_id]);

  useEffect(() => {
    fetchQueueCounts();

    const onQueueCountsChanged = () => {
      fetchQueueCounts();
    };
    const onWindowFocus = () => {
      fetchQueueCounts();
    };

    window.addEventListener(
      'careit:queue-counts-changed',
      onQueueCountsChanged
    );
    window.addEventListener('focus', onWindowFocus);

    return () => {
      window.removeEventListener(
        'careit:queue-counts-changed',
        onQueueCountsChanged
      );
      window.removeEventListener('focus', onWindowFocus);
    };
  }, [fetchQueueCounts]);

  // Distributions address-audit badge (missing map pins)
  useEffect(() => {
    let cancelled = false;
    const loadGeocodeBadge = async () => {
      const canAdmin = canUseDistributionAdminForUser({
        user,
        isFoodMoverFull,
        hasHomeDelivery,
        plan,
      });
      if (!canAdmin) {
        if (!cancelled) setDistributionGeocodeIssues(0);
        return;
      }
      try {
        const res = await distributionsApi.getGeocodeAudit();
        // Hubs require coords at save; badge is household pins only.
        const total = Number(res?.counts?.householdsMissing || 0);
        if (!cancelled) setDistributionGeocodeIssues(total);
      } catch {
        if (!cancelled) setDistributionGeocodeIssues(0);
      }
    };
    loadGeocodeBadge();
    return () => {
      cancelled = true;
    };
  }, [user, isFoodMoverFull, hasHomeDelivery, plan]);

  const { data: freshcueOrganizations = [] } = useFreshcueOrganizations();
  const hasFreshcueOrganizations = freshcueOrganizations.length > 0;
  const totalPendingQueueCount =
    pendingApprovalQueueCount +
    pendingOrgUpdateQueueCount +
    pendingCorporateOnboardingQueueCount +
    pendingSellerApplicationsCount +
    openComplaintQueueCount;
  const renderQueueBadge = (count) =>
    count > 0 ? (
      <Chip
        label={count}
        size="small"
        color="error"
        sx={{
          height: 20,
          minWidth: 20,
          '& .MuiChip-label': {
            px: 0.75,
            fontSize: '0.75rem',
            fontWeight: 700,
            lineHeight: 1,
          },
        }}
      />
    ) : null;

  if (user?.is_individual) {
    return [];
  }

  return [
    // MY ORGANIZATION
    {
      subheader: '',
      items: (user) => {
        const plan = getActivePlan(user);
        const rawPlanKey =
          getPlanBySubscriptionProductId(user?.subscription_product_id) ?? plan;
        const { homeDeliveryAddon: legacyBundleAddon } =
          normalizePlanKey(rawPlanKey);
        const homeDeliveryAddon = Boolean(
          legacyBundleAddon ||
          user?.home_delivery_addon === true ||
          user?.home_delivery_addon === 1
        );
        const isFoodMover = isOpsPlan(plan, { homeDeliveryAddon });
        const isFoodMoverFull = hasPlanCapability(plan, 'proOps');
        const hasHomeDelivery = hasEffectiveHomeDelivery(rawPlanKey, {
          homeDeliveryAddon,
          distributionsEnabled:
            user?.distributions_enabled === true ||
            user?.distributions_enabled === 1,
        });
        const isFreshcue = plan === 'freshcue' || !!user?.freshcue_enabled;
        const isDriverOnly = isDistributionDriverOnlyUser(user);
        const canUseDistributionAdmin = canUseDistributionAdminForUser({
          user,
          isFoodMoverFull,
          hasHomeDelivery,
          plan,
        });
        const canUseDistributionFeature = canUseDistributionsForUser({
          user,
          isFoodMoverFull,
          hasHomeDelivery,
          plan,
        });
        const canUseDistributionReports = canUseDistributionAdmin;
        const hasInventoryAccess =
          !isDriverOnly && (!user.is_non_profit || isFoodMover);

        // Base items that appear for all non-admin users
        const baseItems =
          user?.account_type !== 'ADMIN' && invites?.length
            ? [
                {
                  title: t('account.invitations'),
                  href: PATH_DASHBOARD.settings.account('invitations'),
                  icon: ICONS.invitations,
                  info: <InvitationsBadge location="center" />,
                },
              ]
            : [];

        switch (user.account_type) {
          case 'TEAM-STAFF':
            if (isDriverOnly) {
              return [
                ...baseItems,
                {
                  title: t('sidebar.overview'),
                  href: PATH_DASHBOARD.root,
                  icon: ICONS.dashboard,
                },
                {
                  title: t('sidebar.my-impact'),
                  href: PATH_DASHBOARD.impact,
                  icon: <Public />,
                },
                {
                  title: t('sidebar.calendar'),
                  href: PATH_DASHBOARD.schedule.root,
                  icon: ICONS.calendar,
                },
                ...(!user.is_non_profit
                  ? [
                      {
                        title: t('sidebar.donations.title'),
                        href: PATH_DASHBOARD.donations,
                        icon: ICONS.donation,
                      },
                    ]
                  : []),
                // Drivers on a Distributions-enabled team: put Distributions
                // above Rescues so delivery work is first.
                ...(canUseDistributionFeature
                  ? [
                      {
                        title: t('sidebar.distributions.title'),
                        icon: <VolunteerActivism />,
                        href: PATH_DASHBOARD.distributions.availableRoutes,
                        items: [
                          {
                            title: t('sidebar.distributions.available-routes'),
                            href: PATH_DASHBOARD.distributions.availableRoutes,
                          },
                          {
                            title: t('sidebar.distributions.my-routes'),
                            href: PATH_DASHBOARD.distributions.myRoutes,
                          },
                        ],
                      },
                    ]
                  : []),
                {
                  title: t('sidebar.rescues.title'),
                  icon: <LocalShipping />,
                  href: PATH_DASHBOARD.rescues,
                  items: [
                    {
                      title: t('sidebar.rescues.my-rescues'),
                      href: PATH_DASHBOARD.myrescues,
                    },
                    {
                      title: t('sidebar.rescues.incomplete-rescues'),
                      href: PATH_DASHBOARD.incompleteRescues,
                    },
                  ],
                },
                ...(hasPlanCapability(plan, 'foodRuns')
                  ? [
                      {
                        title: t('sidebar.food-runs.title'),
                        icon: ICONS.animate,
                        href: PATH_DASHBOARD.foodRun.myRuns,
                        items: [
                          {
                            title: t('sidebar.food-runs.my-runs'),
                            href: PATH_DASHBOARD.foodRun.myRuns,
                          },
                          {
                            title: t('sidebar.food-runs.available'),
                            href: PATH_DASHBOARD.foodRun.availableRuns,
                          },
                          {
                            title: t('sidebar.food-runs.in-progress'),
                            href: PATH_DASHBOARD.foodRun.inProgress,
                          },
                          {
                            title: t('sidebar.food-runs.history'),
                            href: PATH_DASHBOARD.foodRun.runHistory,
                          },
                        ],
                      },
                    ]
                  : []),
              ];
            }
            if (user.is_executive) {
              const inspectionsEnabled = isInspectionsEnabled(user);
              const complaintsEnabled = isComplaintsEnabled(user);
              const novEnabled = isNovEnabled(user);
              const penaltiesEnabled = isPenaltiesEnabled(user);
              const schedulingEnabled = isSchedulingEnabled(user);
              const contactLogEnabled = isContactLogEnabled(user);
              const donationsEnabled = isDonationsEnabled(user);
              // Base items for all executive users
              const executiveOnboardingLinks = onboardingEnabled
                ? [
                    {
                      title: t('sidebar.onboarding.title'),
                      href: PATH_DASHBOARD.corporate.onboarding.root,
                      icon: ICONS.assignment,
                      items: [
                        {
                          title: t('sidebar.onboarding.compliance-profiles'),
                          href: PATH_DASHBOARD.corporate.onboarding
                            .complianceProfile.root,
                        },
                        {
                          title: t('sidebar.onboarding.requests'),
                          href: PATH_DASHBOARD.corporate.onboarding.requests,
                        },
                        {
                          title: t('sidebar.executive-onboarding-intake'),
                          href: PATH_DASHBOARD.executive.onboardingRequests,
                        },
                        {
                          title: t('sidebar.onboarding.all-items'),
                          href: PATH_DASHBOARD.corporate.onboarding.root,
                        },
                      ],
                    },
                  ]
                : [];
              const baseExecutiveItems = [
                ...baseItems,
                {
                  title: t('sidebar.home'),
                  href: PATH_DASHBOARD.executive.home,
                  icon: ICONS.dashboard,
                },
                {
                  title: t('sidebar.organizations'),
                  href: PATH_DASHBOARD.executive.orgs,
                  icon: ICONS.company,
                },
                ...(schedulingEnabled
                  ? [
                      {
                        title: t('sidebar.scheduling.title'),
                        href: PATH_DASHBOARD.executive.scheduling.appointments,
                        icon: ICONS.calendar,
                        items: [
                          {
                            title: t('sidebar.scheduling.appointments'),
                            href: PATH_DASHBOARD.executive.scheduling
                              .appointments,
                          },
                          {
                            title: t('sidebar.calendar'),
                            href: PATH_DASHBOARD.executive.scheduling.calendar,
                          },
                        ],
                      },
                    ]
                  : []),
                ...(inspectionsEnabled
                  ? [
                      {
                        title: t('sidebar.inspections.title'),
                        href: PATH_DASHBOARD.executive.inspections.all,
                        icon: <FactCheck />,
                        items: [
                          {
                            title: t('sidebar.inspections.all'),
                            href: PATH_DASHBOARD.executive.inspections.all,
                          },
                          {
                            title: t('sidebar.inspections.mine'),
                            href: PATH_DASHBOARD.executive.inspections.mine,
                          },
                          {
                            title: t('sidebar.inspections.incomplete'),
                            href: PATH_DASHBOARD.executive.inspections
                              .incomplete,
                          },
                        ],
                      },
                    ]
                  : []),
                ...(complaintsEnabled
                  ? [
                      {
                        title: t('sidebar.complaints.title'),
                        href: PATH_DASHBOARD.executive.complaints.all,
                        icon: <Report />,
                        items: [
                          {
                            title: t('sidebar.complaints.all'),
                            href: PATH_DASHBOARD.executive.complaints.all,
                          },
                          {
                            title: t('sidebar.complaints.mine'),
                            href: PATH_DASHBOARD.executive.complaints.mine,
                          },
                          {
                            title: t('sidebar.complaints.incomplete'),
                            href: PATH_DASHBOARD.executive.complaints
                              .incomplete,
                          },
                        ],
                      },
                    ]
                  : []),
                ...(novEnabled
                  ? [
                      {
                        title: 'NOV',
                        href: PATH_DASHBOARD.executive.nov.recommendedReport,
                        icon: ICONS.nov,
                        items: [
                          {
                            title: t('sidebar.nov.recommended-report'),
                            href: PATH_DASHBOARD.executive.nov
                              .recommendedReport,
                          },
                          {
                            title: t('sidebar.nov.issued-report'),
                            href: PATH_DASHBOARD.executive.nov.issuedReport,
                          },
                        ],
                      },
                    ]
                  : []),
                ...(penaltiesEnabled
                  ? [
                      {
                        title: t('sidebar.penalties.title'),
                        href: PATH_DASHBOARD.executive.penalties.log,
                        icon: ICONS.warning,
                        items: [
                          {
                            title: t('sidebar.penalties.log'),
                            href: PATH_DASHBOARD.executive.penalties.log,
                          },
                          {
                            title: t('sidebar.penalties.recommended'),
                            href: PATH_DASHBOARD.executive.penalties
                              .recommended,
                          },
                          {
                            title: t('sidebar.penalties.history'),
                            href: PATH_DASHBOARD.executive.penalties.history,
                          },
                        ],
                      },
                    ]
                  : []),
                ...executiveOnboardingLinks,
              ];
              const reportItems = [
                ...(inspectionsEnabled
                  ? [
                      {
                        title: t('sidebar.inspections.fro'),
                        href: PATH_DASHBOARD.executive.inspections.reports.fro,
                      },
                      {
                        title: t('sidebar.inspections.efg'),
                        href: PATH_DASHBOARD.executive.inspections.reports.efg,
                      },
                    ]
                  : []),
                ...(complaintsEnabled
                  ? [
                      {
                        title: t('sidebar.complaints.title'),
                        href: PATH_DASHBOARD.executive.complaints.report,
                      },
                    ]
                  : []),
                ...(contactLogEnabled
                  ? [
                      {
                        title: t('sidebar.reports.contact-summary'),
                        href: PATH_DASHBOARD.executive.contactSummary,
                      },
                      {
                        title: t('sidebar.reports.compliance-summary'),
                        href: PATH_DASHBOARD.executive.complianceSummary,
                      },
                      {
                        title: t('sidebar.reports.contact-log'),
                        href: PATH_DASHBOARD.executive.contactLog,
                      },
                    ]
                  : []),
                {
                  title: t('sidebar.reports.activity-log'),
                  href: PATH_DASHBOARD.executive.activityLog,
                },
              ];

              // Staff/Admin users get full access
              return [
                ...baseExecutiveItems,
                ...(donationsEnabled
                  ? [
                      {
                        title: t('sidebar.donations.title'),
                        href: PATH_DASHBOARD.executive.donations.all,
                        icon: ICONS.donation,
                        items: [
                          {
                            title: t('sidebar.donations.all-donations'),
                            href: PATH_DASHBOARD.executive.donations.all,
                          },
                          {
                            title: t('sidebar.donations.internal'),
                            href: PATH_DASHBOARD.executive.donations.internal,
                          },
                          {
                            title: t('sidebar.donations.incoming'),
                            href: PATH_DASHBOARD.executive.donations.incoming,
                          },
                          {
                            title: t('sidebar.donations.outgoing'),
                            href: PATH_DASHBOARD.executive.donations.outgoing,
                          },
                        ],
                      },
                    ]
                  : []),
                ...(reportItems.length
                  ? [
                      {
                        title: t('sidebar.reports.title'),
                        href: reportItems[0]?.href,
                        icon: ICONS.analytics,
                        items: reportItems,
                      },
                    ]
                  : []),
              ];
            }
            if (user.is_corporate) {
              return [
                ...baseItems,
                {
                  title: t('sidebar.home'),
                  href: PATH_DASHBOARD.corporate.home,
                  icon: ICONS.dashboard,
                },
                {
                  title: t('sidebar.donations.title'),
                  href: PATH_DASHBOARD.corporate.donations,
                  icon: ICONS.donation,
                },
                {
                  title: t('sidebar.schedule'),
                  href: PATH_DASHBOARD.corporate.schedule,
                  icon: ICONS.calendar,
                },
                {
                  title: t('sidebar.teams'),
                  href: PATH_DASHBOARD.corporate.teams,
                  icon: ICONS.company,
                },
                {
                  title: t('sidebar.recipients'),
                  href: PATH_DASHBOARD.corporate.recipients,
                  icon: ICONS.foodBank,
                },
                {
                  title: t('sidebar.inventory'),
                  href: PATH_DASHBOARD.inventory,
                  icon: ICONS.inventory,
                },
                {
                  title: t('sidebar.reports.title'),
                  href: PATH_DASHBOARD.corporate.report,
                  icon: ICONS.analytics,
                  items: [
                    {
                      title: t('sidebar.reports.non-donating-locations'),
                      href: PATH_DASHBOARD.corporate.nonDonatingLocationsReport,
                    },
                    {
                      title: t('sidebar.reports.donor-team-onboarding'),
                      href: PATH_DASHBOARD.corporate.donorTeamOnboardingReport,
                    },
                    {
                      title: t('sidebar.reports.donations'),
                      href: PATH_DASHBOARD.corporate.report,
                    },
                    {
                      title: t('waste.report'),
                      href: PATH_DASHBOARD.corporate.wasteReport,
                    },
                    {
                      title: t('sidebar.reports.cancellations'),
                      href: PATH_DASHBOARD.corporate.cancellationsReport,
                    },
                    {
                      title: t('sidebar.reports.tax-receipts'),
                      href: PATH_DASHBOARD.corporate.taxReceipts,
                    },
                  ],
                },
                ...(onboardingEnabled
                  ? [
                      {
                        title: t('sidebar.onboarding.title'),
                        href: PATH_DASHBOARD.corporate.onboarding.root,
                        icon: ICONS.assignment,
                        items: [
                          {
                            title: t('sidebar.onboarding.compliance-profiles'),
                            href: PATH_DASHBOARD.corporate.onboarding
                              .complianceProfile.root,
                          },
                          {
                            title: t('sidebar.onboarding.requests'),
                            href: PATH_DASHBOARD.corporate.onboarding.requests,
                          },
                          {
                            title: t('sidebar.executive-onboarding-intake'),
                            href: PATH_DASHBOARD.corporate.onboarding
                              .intakeQueue,
                          },
                          {
                            title: t('sidebar.onboarding.all-items'),
                            href: PATH_DASHBOARD.corporate.onboarding.root,
                          },
                        ],
                      },
                    ]
                  : []),
              ];
            }

            const canCreateFreshcuePosts =
              hasFreshcueOrganizations ||
              isFoodMoverFull ||
              !!user.freshcue_donor_enabled;

            const freshcueItem = {
              title: t('sidebar.freshcue.title'),
              icon: ICONS.freshcue,
              href: PATH_DASHBOARD.freshcue.root,
              allowCollapse: true, // Do not show when no children are visible
              items: [
                {
                  title: t('sidebar.freshcue.all-offers'),
                  href: PATH_DASHBOARD.freshcue.allPosts,
                  cond: isFreshcue,
                },
                {
                  title: t('sidebar.freshcue.manage-members'),
                  href: PATH_DASHBOARD.freshcue.manageMembers,
                  cond: isFreshcue,
                },
                {
                  title: t('sidebar.freshcue.donors'),
                  href: PATH_DASHBOARD.freshcue.donors,
                  cond: isFreshcue,
                },
                {
                  title: t('sidebar.freshcue.donor-stats'),
                  href: PATH_DASHBOARD.freshcue.donorStats,
                  cond: isFreshcue,
                },
                {
                  title: t('sidebar.freshcue.my-offers'),
                  href: PATH_DASHBOARD.freshcue.businessPosts,
                  cond: canCreateFreshcuePosts,
                },
                {
                  title: t('sidebar.freshcue.reservations'),
                  href: PATH_DASHBOARD.freshcue.businessReservations,
                  cond: canCreateFreshcuePosts,
                },
              ].filter(({ cond = true }) => cond),
            };

            if (user.is_non_profit) {
              return [
                ...baseItems,
                {
                  title: t('sidebar.overview'),
                  href: PATH_DASHBOARD.root,
                  icon: ICONS.dashboard,
                },
                {
                  title: t('sidebar.my-impact'),
                  href: PATH_DASHBOARD.impact,
                  icon: <Public />,
                },
                {
                  title: t('sidebar.calendar'),
                  href: PATH_DASHBOARD.schedule.root,
                  icon: ICONS.calendar,
                },
                ...(hasPlanCapability(plan, 'foodRuns')
                  ? [
                      {
                        title: t('sidebar.food-runs.title'),
                        icon: ICONS.animate,
                        href: PATH_DASHBOARD.foodRun.allRuns,
                        items: [
                          {
                            title: t('sidebar.food-runs.all-runs'),
                            href: PATH_DASHBOARD.foodRun.allRuns,
                          },
                          {
                            title: t('sidebar.food-runs.my-runs'),
                            href: PATH_DASHBOARD.foodRun.myRuns,
                          },
                          {
                            title: t('sidebar.food-runs.to-be-reassigned'),
                            href: PATH_DASHBOARD.foodRun.toBeReassigned,
                          },
                          {
                            title: t('sidebar.food-runs.available'),
                            href: PATH_DASHBOARD.foodRun.availableRuns,
                          },
                          {
                            title: t('sidebar.food-runs.in-progress'),
                            href: PATH_DASHBOARD.foodRun.inProgress,
                          },
                          {
                            title: t('sidebar.food-runs.history'),
                            href: PATH_DASHBOARD.foodRun.runHistory,
                          },
                        ],
                      },
                    ]
                  : []),
                ...(canUseDistributionAdmin
                  ? [
                      {
                        title: t('sidebar.distributions.title'),
                        icon: <VolunteerActivism />,
                        href: PATH_DASHBOARD.distributions.root,
                        items: [
                          {
                            title: t('sidebar.distributions.overview'),
                            href: PATH_DASHBOARD.distributions.root,
                          },
                          {
                            title: t('sidebar.distributions.routes'),
                            href: PATH_DASHBOARD.distributions.runs,
                          },
                          {
                            title: t('sidebar.distributions.available-routes'),
                            href: PATH_DASHBOARD.distributions.availableRoutes,
                          },
                          {
                            title: t('sidebar.distributions.my-routes'),
                            href: PATH_DASHBOARD.distributions.myRoutes,
                          },
                          {
                            title: t('sidebar.distributions.clients'),
                            href: PATH_DASHBOARD.distributions.clients,
                            // Geocode issues are fixed from Clients, so the
                            // badge lives on that item only.
                            info: renderQueueBadge(distributionGeocodeIssues),
                          },
                          {
                            title: t('sidebar.distributions.programs'),
                            href: PATH_DASHBOARD.distributions.programs,
                          },
                        ],
                      },
                    ]
                  : []),
                {
                  title: t('sidebar.rescues.title'),
                  icon: <LocalShipping />,
                  badge: IncomingRequestsBadge,
                  href: PATH_DASHBOARD.rescues,
                  items: [
                    {
                      title: t('sidebar.rescues.all-rescues'),
                      href: PATH_DASHBOARD.rescues,
                    },
                    {
                      title: t('sidebar.rescues.my-rescues'),
                      href: PATH_DASHBOARD.myrescues,
                    },
                    {
                      title: t('sidebar.rescues.incomplete-rescues'),
                      href: PATH_DASHBOARD.incompleteRescues,
                    },
                    {
                      title: t('sidebar.rescues.incoming-requests'),
                      href: PATH_DASHBOARD.incomingRequests,
                      icon: ICONS.incoming,
                      info: <IncomingRequestsBadge location="center" />,
                    },
                  ],
                },
                {
                  title: t('sidebar.donations.title'),
                  icon: ICONS.donation,
                  href: PATH_DASHBOARD.donations,
                  items: [
                    {
                      title: t('sidebar.donations.all-donations'),
                      href: PATH_DASHBOARD.donations,
                    },
                    {
                      title: t('sidebar.donations.my-donations'),
                      href: PATH_DASHBOARD.mydonations,
                    },
                    {
                      title: t('sidebar.donations.incomplete'),
                      href: PATH_DASHBOARD.incompleteDonations,
                    },
                  ],
                },
                ...(!user.marketplace_hidden && marketplaceRelevant
                  ? [
                      user.marketplace_enabled
                        ? getMarketplaceSidebarItem({ user, t })
                        : getMarketplaceBuyerSidebarItem({ t }),
                    ]
                  : []),
                {
                  title: t('sidebar.donors'),
                  href: PATH_DASHBOARD.business,
                  icon: ICONS.company,
                },
                {
                  title: t('sidebar.recipients'),
                  href: PATH_DASHBOARD.npo,
                  icon: ICONS.foodBank,
                },
                ...(hasInventoryAccess
                  ? [
                      {
                        title: t('sidebar.inventory'),
                        href: PATH_DASHBOARD.inventory,
                        icon: ICONS.inventory,
                      },
                    ]
                  : []),
                {
                  title: t('sidebar.partnerships'),
                  icon: (
                    <OnboardingRequestsBadge>
                      {ICONS.groups}
                    </OnboardingRequestsBadge>
                  ),
                  href: PATH_DASHBOARD.partnerships,
                },
                {
                  title: t('sidebar.reports.title'),
                  icon: ICONS.analytics,
                  href: PATH_DASHBOARD.reports,
                  items: [
                    {
                      title: t('sidebar.reports.cancellations'),
                      href: PATH_DASHBOARD.cancellationReport,
                    },
                    ...(isFoodMoverFull
                      ? [
                          {
                            title: t('sidebar.reports.donations'),
                            href: PATH_DASHBOARD.donationsReport,
                          },
                          ...(canUseDistributionReports
                            ? [
                                {
                                  title: t('sidebar.reports.distributions'),
                                  href: PATH_DASHBOARD.distributionReports,
                                },
                              ]
                            : []),
                          {
                            title: t('sidebar.reports.donor-tax-receipts'),
                            href: PATH_DASHBOARD.foodMoverTaxReceipts,
                          },
                        ]
                      : []),
                    {
                      title: t('sidebar.reports.drivers'),
                      href: PATH_DASHBOARD.drivers,
                    },
                  ],
                },
                freshcueItem,
              ];
            }

            return [
              ...baseItems,
              {
                title: t('sidebar.overview'),
                href: PATH_DASHBOARD.root,
                icon: ICONS.dashboard,
              },
              {
                title: t('sidebar.my-impact'),
                href: PATH_DASHBOARD.impact,
                icon: <Public />,
              },
              {
                title: t('sidebar.calendar'),
                href: PATH_DASHBOARD.schedule.root,
                icon: ICONS.calendar,
              },
              {
                title: t('sidebar.donations.all-donations'),
                href: PATH_DASHBOARD.donations,
                icon: ICONS.donation,
              },
              {
                title: t('sidebar.donations.my-donations'),
                href: PATH_DASHBOARD.mydonations,
                icon: ICONS.donation,
              },
              {
                title: t('sidebar.donations.incomplete'),
                icon: <RunningWithErrors />,
                href: PATH_DASHBOARD.incompleteDonations,
              },
              ...(hasInventoryAccess ||
              corporateInfo?.isPartOfACorporate ||
              hasFreshcueOrganizations
                ? [
                    {
                      title: t('sidebar.inventory'),
                      href: PATH_DASHBOARD.inventory,
                      icon: ICONS.inventory,
                    },
                  ]
                : []),
              ...(!user.marketplace_hidden
                ? [getMarketplaceSidebarItem({ user, t })]
                : []),
              {
                title: t('sidebar.partnerships'),
                href: PATH_DASHBOARD.partnerships,
                icon: ICONS.groups,
              },
              {
                title: t('sidebar.corporate-onboarding'),
                icon: (
                  <OnboardingRequestsBadge>
                    {ICONS.company}
                  </OnboardingRequestsBadge>
                ),
                href: PATH_DASHBOARD.corporateOnboarding.donorOnboarding,
                items: [
                  {
                    title: t('sidebar.corporate-onboarding'),
                    href: PATH_DASHBOARD.corporateOnboarding.donorOnboarding,
                  },
                  {
                    title: t('sidebar.executive-onboarding-intake'),
                    href: PATH_DASHBOARD.corporate.onboarding.intakeQueue,
                  },
                ],
              },
              {
                title: t('sidebar.reports.title'),
                icon: ICONS.analytics,
                href: PATH_DASHBOARD.reports,
                items: [
                  {
                    title: t('sidebar.reports.cancellations'),
                    href: PATH_DASHBOARD.cancellationReport,
                  },
                  {
                    title: t('sidebar.reports.drivers'),
                    href: PATH_DASHBOARD.drivers,
                  },
                  {
                    title: t('sidebar.reports.tax-receipts'),
                    href: PATH_DASHBOARD.taxReceipts,
                  },
                  ...(corporateInfo?.isPartOfACorporate ||
                  hasFreshcueOrganizations
                    ? [
                        {
                          title: t(
                            'sidebar.reports.waste-report',
                            'Waste Report'
                          ),
                          href: PATH_DASHBOARD.wasteTracking,
                        },
                      ]
                    : []),
                ],
              },
              freshcueItem,
            ];

          case 'ADMIN': {
            const isOwner = !!user.is_owner;
            let perms = [];
            try {
              perms =
                typeof user.admin_permissions === 'string'
                  ? JSON.parse(user.admin_permissions) || []
                  : user.admin_permissions || [];
            } catch {
              /* ignored */
            }
            // Check for impersonation override
            const impersonation = isOwner ? getAdminImpersonation() : null;
            let impersonating = false;
            if (impersonation) {
              perms = impersonation.permissions || [];
              impersonating = true;
            }
            const hasPerm = (key) =>
              (isOwner && !impersonating) || perms.includes(key);
            return [
              {
                title: t('sidebar.overview'),
                icon: <Equalizer />,
                href: PATH_DASHBOARD.admin.overview,
              },
              {
                title: t('sidebar.approval-queue'),
                icon: <Assignment />,
                href: PATH_DASHBOARD.admin.approvalQueue,
                info: renderQueueBadge(totalPendingQueueCount),
                items: [
                  {
                    title: t('sidebar.admin.new-organizations'),
                    href: PATH_DASHBOARD.admin.approvalQueue,
                    info: renderQueueBadge(pendingApprovalQueueCount),
                  },
                  {
                    title: t('sidebar.admin.organization-updates'),
                    href: PATH_DASHBOARD.admin.orgUpdateQueue,
                    info: renderQueueBadge(pendingOrgUpdateQueueCount),
                  },
                  {
                    title: t('sidebar.executive-onboarding-intake'),
                    href: PATH_DASHBOARD.admin.onboardingIntakeQueue,
                    info: renderQueueBadge(
                      pendingCorporateOnboardingQueueCount
                    ),
                  },
                  {
                    title: t('sidebar.admin.marketplace-sellers'),
                    href: PATH_DASHBOARD.admin.marketplaceSellers,
                    info: renderQueueBadge(pendingSellerApplicationsCount),
                  },
                  {
                    title: t('sidebar.complaints.title'),
                    href: PATH_DASHBOARD.admin.complaintsQueue,
                    info: renderQueueBadge(openComplaintQueueCount),
                  },
                ],
              },
              {
                title: t('sidebar.organizations'),
                icon: ICONS.company,
                href: PATH_DASHBOARD.admin.orgs,
                items: [
                  {
                    title: 'Directory',
                    href: PATH_DASHBOARD.admin.orgs,
                  },
                  {
                    title: 'Maps',
                    href: PATH_DASHBOARD.admin.map,
                    alsoActiveOn: [PATH_DASHBOARD.admin.outreach],
                  },
                  {
                    title: t('sidebar.admin.ein-status'),
                    href: PATH_DASHBOARD.admin.einStatus,
                  },
                ],
              },
              ...(hasPerm('licensing')
                ? [
                    {
                      title: t('sidebar.admin.licensing'),
                      icon: <CardMembership />,
                      href: PATH_DASHBOARD.admin.licensing.clients,
                      items: [
                        ...(isOwner && !impersonating
                          ? [
                              {
                                title: t('sidebar.overview'),
                                href: PATH_DASHBOARD.admin.licensing.overview,
                                info: ownerOnlyStar,
                              },
                            ]
                          : []),
                        {
                          title: t('sidebar.admin.clients'),
                          href: PATH_DASHBOARD.admin.licensing.clients,
                        },
                        {
                          title: t('sidebar.admin.subscriptions'),
                          href: PATH_DASHBOARD.admin.subscriptions,
                        },
                        {
                          title: t('sidebar.admin.coverage-map'),
                          href: PATH_DASHBOARD.admin.licensing.map,
                        },
                        ...(isOwner && !impersonating
                          ? [
                              {
                                title: t('sidebar.schedule'),
                                href: PATH_DASHBOARD.admin.licensing.schedule,
                                info: ownerOnlyStar,
                              },
                            ]
                          : []),
                      ],
                    },
                  ]
                : []),
              {
                title: t('sidebar.admin.accounts'),
                icon: <AccountBalance />,
                href: PATH_DASHBOARD.admin.executiveUsers,
                items: [
                  {
                    title: t('sidebar.executive'),
                    href: PATH_DASHBOARD.admin.executiveUsers,
                  },
                  {
                    title: t('sidebar.corporate'),
                    href: PATH_DASHBOARD.admin.corporateAccounts,
                  },
                ],
              },
              {
                title: t('sidebar.admin.users'),
                href: PATH_DASHBOARD.admin.users,
                icon: <Groups />,
              },
              {
                title: t('sidebar.admin.white-label'),
                href: PATH_DASHBOARD.admin.brands,
                icon: <Palette />,
              },
              ...(hasPerm('newsletters')
                ? [
                    {
                      title: 'Newsletters',
                      icon: <Campaign />,
                      href: PATH_DASHBOARD.admin.newsletters.root,
                      items: [
                        {
                          title: 'Campaigns',
                          href: PATH_DASHBOARD.admin.newsletters.root,
                        },
                        {
                          title: 'Automations',
                          href: PATH_DASHBOARD.admin.newsletters.automations,
                        },
                        {
                          title: 'Subscribers',
                          href: PATH_DASHBOARD.admin.newsletters.subscribers,
                        },
                        {
                          title: 'Groups',
                          href: PATH_DASHBOARD.admin.newsletters.groups,
                        },
                        {
                          title: 'Templates',
                          href: PATH_DASHBOARD.admin.newsletters.templates,
                        },
                        {
                          title: 'Deliverability',
                          href: PATH_DASHBOARD.admin.newsletters.deliverability,
                        },
                        {
                          title: 'Activity log',
                          href: PATH_DASHBOARD.admin.newsletters.activity,
                        },
                      ],
                    },
                  ]
                : []),
              ...(hasPerm('crm')
                ? [
                    {
                      title: 'CRM',
                      icon: <Handshake />,
                      href: PATH_DASHBOARD.admin.crm.root,
                      items: [
                        ...(isOwner && !impersonating
                          ? [
                              {
                                title: t('common.summary'),
                                href: PATH_DASHBOARD.admin.crm.summary,
                                info: ownerOnlyStar,
                              },
                            ]
                          : []),
                        {
                          title: t('sidebar.admin.prospecting'),
                          href: PATH_DASHBOARD.admin.crm.prospecting,
                        },
                        {
                          title: t('sidebar.admin.leads'),
                          href: PATH_DASHBOARD.admin.crm.leads,
                        },
                        {
                          title: t('sidebar.admin.pipeline-board'),
                          href: PATH_DASHBOARD.admin.crm.root,
                        },
                        {
                          title: t('sidebar.admin.companies'),
                          href: PATH_DASHBOARD.admin.crm.companies,
                        },
                        {
                          title: t('sidebar.admin.contacts'),
                          href: PATH_DASHBOARD.admin.crm.contacts,
                        },
                        {
                          title: t('sidebar.admin.tasks'),
                          href: PATH_DASHBOARD.admin.crm.tasks,
                        },
                      ],
                    },
                  ]
                : []),
              {
                title: t('sidebar.admin.activity'),
                icon: ICONS.assignment,
                href: PATH_DASHBOARD.donations,
                items: [
                  {
                    title: t('sidebar.donations.title'),
                    href: PATH_DASHBOARD.donations,
                  },
                  {
                    title: t('sidebar.rescues.title'),
                    href: PATH_DASHBOARD.rescues,
                  },
                  {
                    title: t('sidebar.food-runs.title'),
                    href: PATH_DASHBOARD.foodRun.allRuns,
                  },
                  {
                    title: t('sidebar.distributions.title'),
                    href: PATH_DASHBOARD.admin.distributions.root,
                  },
                  {
                    title: t('sidebar.admin.cancellations'),
                    href: PATH_DASHBOARD.admin.cancellations,
                  },
                  {
                    title: t('sidebar.inventory'),
                    href: PATH_DASHBOARD.admin.inventory,
                  },
                ],
              },
              {
                title: t('marketplace.title'),
                icon: ICONS.ecommerce,
                href: PATH_DASHBOARD.admin.marketplaceGeneral,
                items: [
                  {
                    title: t('sidebar.admin.general'),
                    href: PATH_DASHBOARD.admin.marketplaceGeneral,
                  },
                  {
                    title: t('sidebar.admin.sales'),
                    href: PATH_DASHBOARD.admin.marketplaceSales,
                  },
                  {
                    title: t('sidebar.admin.listings'),
                    href: PATH_DASHBOARD.admin.marketplaceListings,
                  },
                  // Financial report: owner-only (page also guards in-place).
                  ...(isOwner && !impersonating
                    ? [
                        {
                          title: t('sidebar.admin.reconciliation'),
                          href: PATH_DASHBOARD.admin.marketplaceReconciliation,
                          info: ownerOnlyStar,
                        },
                      ]
                    : []),
                ],
              },
              ...(hasPerm('freshcue')
                ? [
                    {
                      title: 'Freshcue',
                      icon: ICONS.freshcue,
                      href: PATH_DASHBOARD.admin.freshcueWaitlist,
                      items: [
                        {
                          title: t('sidebar.admin.waitlist'),
                          href: PATH_DASHBOARD.admin.freshcueWaitlist,
                        },
                        {
                          title: t('sidebar.admin.notifications'),
                          href: PATH_DASHBOARD.admin.freshcueNotifications,
                        },
                      ],
                    },
                  ]
                : []),
              ...(hasPerm('chat_sms')
                ? [
                    {
                      title: t('sidebar.admin.communication'),
                      icon: ICONS.chat,
                      href: PATH_DASHBOARD.admin.chats,
                      items: [
                        {
                          title: t('sidebar.admin.chats'),
                          href: PATH_DASHBOARD.admin.chats,
                        },
                        { title: 'SMS', href: PATH_DASHBOARD.admin.sms },
                      ],
                    },
                  ]
                : []),
              ...(hasPerm('data')
                ? [
                    {
                      title: t('sidebar.admin.data'),
                      icon: <Storage />,
                      href: PATH_DASHBOARD.admin.imports.root,
                      items: [
                        {
                          title: t('sidebar.admin.import-donations'),
                          href: PATH_DASHBOARD.admin.imports.donations,
                        },
                        {
                          title: t('sidebar.admin.import-users'),
                          href: PATH_DASHBOARD.admin.imports.users,
                        },
                        {
                          title: t('sidebar.admin.deleted-records'),
                          href: PATH_DASHBOARD.admin.deletions.donations,
                        },
                      ],
                    },
                  ]
                : []),
            ];
          }
          default:
            return [];
        }
      },
    },

    // SETTINGS
    {
      subheader: t('sidebar.settings'),
      items: (user) => {
        switch (user.account_type) {
          case 'ADMIN':
            return [];
          default:
            if (user.is_executive) {
              return user.is_admin
                ? [
                    {
                      title: t('sidebar.geozones'),
                      href: PATH_DASHBOARD.executive.geozones,
                      icon: ICONS.map,
                    },
                    {
                      title: t('sidebar.my-team'),
                      href: PATH_DASHBOARD.teams,
                      icon: <People />,
                    },
                    {
                      title: t('sidebar.my-organization'),
                      href: PATH_DASHBOARD.executive.orgSettings,
                      icon: <BusinessCenter />,
                    },
                    {
                      title: t('sidebar.meta-data'),
                      href: PATH_DASHBOARD.settings.tags,
                      icon: ICONS.user,
                    },
                  ]
                : [
                    {
                      title: t('sidebar.geozones'),
                      href: PATH_DASHBOARD.executive.geozones,
                      icon: ICONS.map,
                    },
                  ];
            }

            if (user.is_non_profit) {
              return user.is_admin
                ? [
                    {
                      title: t('sidebar.training-program'),
                      href: PATH_DASHBOARD.trainingEditor,
                      icon: <School />,
                    },
                    {
                      title: t('sidebar.my-team'),
                      href: PATH_DASHBOARD.teams,
                      icon: <People />,
                    },
                    {
                      title: t('sidebar.my-organization'),
                      href: PATH_DASHBOARD.settings.business(''),
                      icon: <BusinessCenter />,
                    },
                    {
                      title: t('sidebar.vehicles'),
                      icon: ICONS.vehicle,
                      href: PATH_DASHBOARD.vehicle,
                    },
                    {
                      title: t('sidebar.documents'),
                      href: PATH_DASHBOARD.settings.documents,
                      icon: <FileCopy />,
                    },
                    {
                      title: t('sidebar.email-settings'),
                      href: PATH_DASHBOARD.customizeEmail,
                      icon: ICONS.mail,
                    },
                    {
                      title: t('sidebar.meta-data'),
                      href: PATH_DASHBOARD.settings.tags,
                      icon: ICONS.user,
                    },
                  ]
                : [
                    {
                      title: t('sidebar.documents'),
                      href: PATH_DASHBOARD.settings.documents,
                      icon: <FileCopy />,
                    },
                    ...(user.is_driver
                      ? []
                      : [
                          {
                            title: t('sidebar.vehicles'),
                            icon: ICONS.vehicle,
                            href: PATH_DASHBOARD.vehicle,
                          },
                        ]),
                  ];
            }

            return user.is_admin
              ? [
                  ...(user.is_safety_program_enabled
                    ? [
                        {
                          title: t('sidebar.training-program'),
                          href: PATH_DASHBOARD.trainingEditor,
                          icon: <School />,
                        },
                      ]
                    : []),
                  {
                    title: t('sidebar.my-team'),
                    href: PATH_DASHBOARD.teams,
                    icon: <People />,
                  },

                  ...(user?.is_corporate
                    ? [
                        {
                          title: t('sidebar.my-organization'),
                          href: PATH_DASHBOARD.corporate.orgSettings,
                          icon: <BusinessCenter />,
                        },
                      ]
                    : [
                        {
                          title: t('sidebar.my-organization'),
                          href: PATH_DASHBOARD.settings.business(''),
                          icon: <BusinessCenter />,
                        },
                      ]),

                  {
                    title: t('sidebar.documents'),
                    href: PATH_DASHBOARD.settings.documents,
                    icon: <FileCopy />,
                  },
                  {
                    title: t('sidebar.vehicles'),
                    icon: ICONS.vehicle,
                    href: PATH_DASHBOARD.vehicle,
                  },
                  {
                    title: t('sidebar.email-settings'),
                    href: PATH_DASHBOARD.customizeEmail,
                    icon: ICONS.mail,
                  },
                  {
                    title: t('sidebar.meta-data'),
                    href: PATH_DASHBOARD.settings.tags,
                    icon: ICONS.user,
                  },
                ]
              : [
                  {
                    title: t('sidebar.documents'),
                    href: PATH_DASHBOARD.settings.documents,
                    icon: <FileCopy />,
                  },
                ];
        }
      },
    },

    {
      subheader: '',
      items: (user) => {
        const items = [
          {
            title:
              user?.account_type === 'ADMIN'
                ? t('sidebar.knowledge-base')
                : t('sidebar.help'),
            icon: <MenuBook />,
            href: PATH_DASHBOARD.kb,
          },
        ];

        if (user?.account_type === 'ADMIN') {
          items.push({
            title: t('sidebar.internal-wiki'),
            icon: <AutoStories />,
            href: PATH_DASHBOARD.wiki,
          });
        }

        return items;
      },
    },

    {
      subheader: '',
      items: (user) => {
        if (user?.account_type !== 'ADMIN') {
          return [];
        }

        const isOwner = !!user.is_owner;
        const impersonating = Boolean(isOwner && getAdminImpersonation());

        return [
          {
            title: t('sidebar.admin.links'),
            icon: <Public />,
            href: 'https://desk.zoho.com',
            items: [
              {
                title: 'Zoho Desk',
                href: 'https://desk.zoho.com',
                crossOrigin: true,
              },
              {
                title: 'Quo Status',
                href: QUO_STATUS_URL,
                crossOrigin: true,
              },
              {
                title: 'ReFED Insights',
                href: 'https://insights.refed.org',
                crossOrigin: true,
              },
            ],
          },
          ...(isOwner && !impersonating
            ? [
                {
                  title: t('sidebar.settings'),
                  icon: <Settings />,
                  href: PATH_DASHBOARD.admin.crm.settings,
                  info: ownerOnlyStar,
                  items: [
                    {
                      title: 'CRM',
                      href: PATH_DASHBOARD.admin.crm.settings,
                      info: ownerOnlyStar,
                    },
                    {
                      title: t('marketplace.title'),
                      href: PATH_DASHBOARD.admin.marketplaceSettings,
                      info: ownerOnlyStar,
                    },
                    {
                      title: t('sidebar.admin.admin-users'),
                      href: PATH_DASHBOARD.admin.adminUsers,
                      info: ownerOnlyStar,
                    },
                    {
                      title: t('sidebar.admin.mobile-apps'),
                      href: PATH_DASHBOARD.admin.mobileApps,
                      info: ownerOnlyStar,
                    },
                  ],
                },
              ]
            : []),
        ];
      },
    },
  ];
};

export default useSidebarConfig;
